import React, { useEffect, useState, useRef } from 'react';
import { MagnifyingGlass, User, List, X } from '@phosphor-icons/react';
import { Logo } from './Logo';
import { Button } from '../ui/Button';
import { CreditsIndicator } from '../credits/CreditsIndicator';
import { PAGE_ROUTE } from '../../lib/routes';
import { cn } from '../../lib/utils';
interface HeaderProps {
  currentPage: string;
  navigate: (page: string, context?: any) => void;
  session?: {
    user?: {
      name?: string;
      email?: string;
    };
    role?: 'demand' | 'supply';
  } | null;
  onSignOut?: () => void;
}
export function Header({
  currentPage,
  navigate,
  session = null,
  onSignOut
}: HeaderProps) {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [burgerMenuOpen, setBurgerMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const burgerMenuRef = useRef<HTMLDivElement>(null);
  // Any dashboard subpage (DASHBOARD_PROFIL, DASHBOARD_APLICARI, ...) — all start with 'DASHBOARD'.
  // Defensive against transient undefined route state during fast navigations.
  const isDashboard = (currentPage || '').startsWith('DASHBOARD');
  const isSupply = session?.role === 'supply';
  const roleLabel = isSupply ? 'Proprietar' : 'Chiriaș';
  const middleMenuLabel = isSupply ? 'Oferte trimise' : 'Oferte primite';
  // Close menus on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (userMenuRef.current && !userMenuRef.current.contains(target)) {
        setUserMenuOpen(false);
      }
      if (burgerMenuRef.current && !burgerMenuRef.current.contains(target)) {
        setBurgerMenuOpen(false);
      }
    };
    if (userMenuOpen || burgerMenuOpen) {
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }
  }, [userMenuOpen, burgerMenuOpen]);
  const go = (page: string) => {
    navigate(page);
    setUserMenuOpen(false);
    setBurgerMenuOpen(false);
  };
  const handleSignOut = () => {
    if (onSignOut) onSignOut();
    setUserMenuOpen(false);
    navigate(PAGE_ROUTE.HOME);
  };
  const iconBtn = 'p-2 rounded-md text-light-2 hover:text-light-1 hover:bg-dark-3 transition-colors cursor-pointer';
  return <header className="sticky top-0 z-50 w-full" data-id="element-2197">
      {/* Inset background — on dashboard, the blur layer doesn't span the sidebar */}
      <div className={cn('bg-dark-1/80 backdrop-blur-md', isDashboard && 'lg:ml-64')} data-id="element-2198">
        <div className="container mx-auto px-4 sm:px-6 h-20 flex items-center justify-between" data-id="element-2199">
          {/* Logo */}
          <Logo onClick={() => go(PAGE_ROUTE.HOME)} data-id="element-2200" />

          {/* Right icon group */}
          <div className="flex items-center gap-1.5 sm:gap-2" data-id="element-2201">
            {/* Search → Marketplace */}
            <button onClick={() => go(PAGE_ROUTE.MARKETPLACE)} className={iconBtn} aria-label="Caut chiriaș" title="Caut chiriaș" data-id="element-2202">
              <MagnifyingGlass size={22} weight="regular" data-id="element-2203" />
            </button>

            {/* Credits indicator — supply role only */}
            {isSupply && <CreditsIndicator navigate={navigate} data-id="element-2204" />}

            {/* User menu */}
            <div ref={userMenuRef} className="relative" data-id="element-2205">
              <button onClick={() => {
              setUserMenuOpen(v => !v);
              setBurgerMenuOpen(false);
            }} className={cn(iconBtn, userMenuOpen && 'bg-dark-3 text-light-1')} aria-label="Cont" aria-expanded={userMenuOpen} title="Cont" data-id="element-2206">
                <User size={22} weight="regular" data-id="element-2207" />
              </button>

              {userMenuOpen && <div className="absolute right-0 mt-2 w-60 rounded-md border border-dark-4 bg-dark-2 shadow-2xl overflow-hidden" data-id="element-2208">
                  {session ? <>
                      <div className="px-4 py-3 border-b border-dark-4" data-id="element-2209">
                        <p className="text-ui-sm text-light-3 truncate" data-id="element-2210">
                          {session.user?.email}
                        </p>
                        <div className="flex items-center justify-between gap-2 mt-0.5" data-id="element-2211">
                          {session.user?.name && <p className="text-ui-md text-light-1 font-medium truncate" data-id="element-2212">
                              {session.user.name}
                            </p>}
                          <span className={cn('shrink-0 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full', isSupply ? 'bg-dark-4 text-light-2 border border-dark-4' : 'bg-lime-5 text-lime-1 border border-lime-4')} data-id="element-2213">
                            {roleLabel}
                          </span>
                        </div>
                      </div>
                      <button onClick={() => go(PAGE_ROUTE.DASHBOARD_PROFIL)} className="w-full text-left px-4 py-2.5 text-ui-md text-light-2 hover:bg-dark-3 hover:text-light-1 transition-colors cursor-pointer" data-id="element-2214">
                        Profilul meu
                      </button>
                      <button onClick={() => go(PAGE_ROUTE.DASHBOARD_APLICARI)} className="w-full text-left px-4 py-2.5 text-ui-md text-light-2 hover:bg-dark-3 hover:text-light-1 transition-colors cursor-pointer" data-id="element-2215">
                        {middleMenuLabel}
                      </button>
                      {isSupply && <button onClick={() => go(PAGE_ROUTE.DASHBOARD_PROFILE_SALVATE)} className="w-full text-left px-4 py-2.5 text-ui-md text-light-2 hover:bg-dark-3 hover:text-light-1 transition-colors cursor-pointer" data-id="element-2216">
                          Profile salvate
                        </button>}
                      <button onClick={() => go(PAGE_ROUTE.DASHBOARD_SETARI)} className="w-full text-left px-4 py-2.5 text-ui-md text-light-2 hover:bg-dark-3 hover:text-light-1 transition-colors cursor-pointer" data-id="element-2217">
                        Setări
                      </button>
                      {isSupply && <button onClick={() => go(PAGE_ROUTE.PRICING)} className="w-full text-left px-4 py-2.5 text-ui-md text-light-2 hover:bg-dark-3 hover:text-light-1 transition-colors cursor-pointer" data-id="element-2218">
                          Pachete & credite
                        </button>}
                      <div className="h-px bg-dark-4" data-id="element-2219" />
                      <button onClick={handleSignOut} className="w-full text-left px-4 py-2.5 text-ui-md text-red-1 hover:bg-red-4 transition-colors cursor-pointer" data-id="element-2220">
                        Ieși din cont
                      </button>
                    </> : <>
                      <button onClick={() => go(PAGE_ROUTE.LOGIN)} className="w-full text-left px-4 py-2.5 text-ui-md text-light-2 hover:bg-dark-3 hover:text-light-1 transition-colors cursor-pointer" data-id="element-2221">
                        Autentificare
                      </button>
                      <button onClick={() => go(PAGE_ROUTE.REGISTER)} className="w-full text-left px-4 py-2.5 text-ui-md text-light-2 hover:bg-dark-3 hover:text-light-1 transition-colors cursor-pointer" data-id="element-2222">
                        Creează cont
                      </button>
                    </>}
                </div>}
            </div>

            {/* Burger menu */}
            <div ref={burgerMenuRef} className="relative" data-id="element-2223">
              <button onClick={() => {
              setBurgerMenuOpen(v => !v);
              setUserMenuOpen(false);
            }} className={cn(iconBtn, burgerMenuOpen && 'bg-dark-3 text-light-1')} aria-label="Meniu" aria-expanded={burgerMenuOpen} title="Meniu" data-id="element-2224">
                {burgerMenuOpen ? <X size={22} weight="regular" data-id="element-2225" /> : <List size={22} weight="regular" data-id="element-2226" />}
              </button>

              {burgerMenuOpen && <div className="absolute right-0 mt-2 w-56 rounded-md border border-dark-4 bg-dark-2 shadow-2xl overflow-hidden" data-id="element-2227">
                  <button onClick={() => go(PAGE_ROUTE.HOME)} className="w-full text-left px-4 py-2.5 text-ui-md text-light-2 hover:bg-dark-3 hover:text-light-1 transition-colors cursor-pointer" data-id="element-2228">
                    Acasă
                  </button>
                  <button onClick={() => go(PAGE_ROUTE.MARKETPLACE)} className="w-full text-left px-4 py-2.5 text-ui-md text-light-2 hover:bg-dark-3 hover:text-light-1 transition-colors cursor-pointer" data-id="element-2229">
                    Marketplace
                  </button>
                  <div className="h-px bg-dark-4" data-id="element-2230" />
                  <button onClick={() => go(PAGE_ROUTE.CONTACT)} className="w-full text-left px-4 py-2.5 text-ui-md text-light-2 hover:bg-dark-3 hover:text-light-1 transition-colors cursor-pointer" data-id="element-2231">
                    Contact
                  </button>
                  <button onClick={() => go(PAGE_ROUTE.TERMENI)} className="w-full text-left px-4 py-2.5 text-ui-md text-light-2 hover:bg-dark-3 hover:text-light-1 transition-colors cursor-pointer" data-id="element-2232">
                    Termeni
                  </button>
                  <button onClick={() => go(PAGE_ROUTE.CONFIDENTIALITATE)} className="w-full text-left px-4 py-2.5 text-ui-md text-light-2 hover:bg-dark-3 hover:text-light-1 transition-colors cursor-pointer" data-id="element-2233">
                    Confidențialitate
                  </button>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </header>;
}